import React from 'react';
import PropTypes from 'prop-types';
import { ButtonBase, Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme, { width, paddingY, paddingX, borderWidth, borderColor, borderRadius, gap, minHeight, labelFontSize, labelFontWeight, labelLineHeight, isActive }) => ({
    root: {
        width: width || 'auto',
        paddingTop: paddingY != null ? paddingY : theme.spacing(0.25),
        paddingBottom: paddingY != null ? paddingY : theme.spacing(0.25),
        paddingLeft: paddingX != null ? paddingX : theme.spacing(1.5),
        paddingRight: paddingX != null ? paddingX : theme.spacing(1.5),
        borderStyle: 'solid',
        borderWidth: borderWidth != null ? borderWidth : theme.spacing(0.125),
        borderColor: borderColor || theme.palette.neutral.black,
        borderRadius: borderRadius != null ? borderRadius : theme.spacing(1),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: gap != null ? gap : theme.spacing(0.75),
        minHeight: minHeight || 'auto',
        color: 'inherit',
        textDecoration: 'none',
        textTransform: 'none',
        // '&:hover': {
        //     backgroundColor: 'transparent',
        //     opacity: theme.palette.action.hoverOpacity,
        // },
    },
    active: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
    },
    primary: {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        // '&&' bumps specificity so the white color wins over the base `root` color:inherit
        // regardless of stylesheet injection order; the descendant rule forces any label
        // Typography (AppButton's own label + custom children like ConnectorActionButton) white.
        '&&': {
            color: '#fff',
        },
        '& .MuiTypography-root': {
            color: '#fff',
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
        },
    },
    disabled: {
        opacity: theme.palette.action.disabledOpacity,
        pointerEvents: 'none',
    },
    iconWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'inherit',
    },
    label: {
        color: 'inherit',
        fontSize: labelFontSize || theme.typography.pxToRem(16),
        fontWeight: labelFontWeight != null
            ? labelFontWeight
            : (isActive ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular),
        lineHeight: labelLineHeight || theme.typography.body1.lineHeight,
        textAlign: 'center',
    },
}));

const AppButton = ({
    label,
    children,
    icon,
    onClick,
    disabled = false,
    className,
    ariaLabel,
    isActive = false,
    variant = 'secondary',
    width,
    paddingY,
    paddingX,
    borderWidth,
    borderColor,
    borderRadius,
    minHeight,
    gap,
    labelFontSize,
    labelFontWeight,
    labelLineHeight,
    underlineWeight,
    component,
    to,
    ...rest
}) => {
    const { classes } = useStyles({
        width,
        isActive,
        paddingY,
        paddingX,
        borderWidth,
        borderColor,
        borderRadius,
        minHeight,
        gap,
        labelFontSize,
        labelFontWeight,
        labelLineHeight,
    });
    const content = children || label;
    const classNames = [classes.root, variant === 'primary' ? classes.primary : '', isActive ? classes.active : '', disabled ? classes.disabled : '', className || '']
        .filter(Boolean)
        .join(' ');

    return (
        <ButtonBase
            className={classNames}
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
            component={component}
            to={to}
            {...rest}>
            {icon ? <Box className={classes.iconWrap}>{icon}</Box> : null}
            {typeof content === 'string' ? (
                <Typography component="span" variant="inherit" className={classes.label}>
                    {content}
                </Typography>
            ) : (
                content
            )}
        </ButtonBase>
    );
};

AppButton.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    ariaLabel: PropTypes.string,
    isActive: PropTypes.bool,
    variant: PropTypes.oneOf(['primary', 'secondary']),
    width: PropTypes.string,
    paddingY: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paddingX: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    borderColor: PropTypes.string,
    borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    labelFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    labelFontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    labelLineHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    underlineWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    component: PropTypes.elementType,
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default AppButton;
